import { SIGNED_OUT_REFUSALS } from '@featit/shared/credentials';
import { deploymentAddressLabel, effectiveDeploymentAddress } from './deploymentAddress';
import {
  failureToSay,
  sentenceToSay,
  serverRefusalText,
  type HeldSentenceType,
} from '@/services/actionFailure';
import { isShellRuntime } from '@/shell/shellDetection';
import { DesktopForeignCodeError } from '@/desktop/desktopSignInErrors';
import { DesktopKeychainUnavailableError } from '@/desktop/desktopSessionErrors';
import type { MessageKey } from '@featit/shared/i18n/messages/index';

/**
 * Telling "the deployment never answered" apart from "the deployment said no".
 *
 * These are different failures with different repairs — fix the address or start the deployment,
 * versus fix the password — and a single "Sign-in failed" sends the reader to the wrong one. Under
 * ADR-050 the address is something a person chose, so being pointed at nothing is now an ordinary
 * mistake rather than an exotic one, and it has to read as itself.
 *
 * The distinguishing fact is whether the deployment produced a response at all. A transport
 * rejection carries a request and no response: nothing judged the credentials, so saying anything
 * about them would be an invention.
 */

interface ITransportFailure {
  isAxiosError?: boolean;
  request?: unknown;
  response?: { status?: number; data?: { code?: unknown; message?: unknown } };
  code?: unknown;
  message?: unknown;
}

/** A failure where nothing at the address answered, so nothing judged the credentials. */
export function isDeploymentUnreachableError(error: unknown): boolean {
  const candidate = error as ITransportFailure | null | undefined;
  if (!candidate || typeof candidate !== 'object') return false;
  if (candidate.response) return false;
  return Boolean(candidate.isAxiosError || candidate.request || candidate.code);
}

function serverMessage(error: unknown): string | null {
  // IN-104: a coded refusal in the reader's language, else the server's sentence.
  return serverRefusalText(error);
}

/**
 * The sentences a refused sign-in carries, as the deployment words them. A refusal that says one of these
 * is the catalogue's own sentence in the reader's language; any other server sentence is shown as it came.
 */
const KNOWN_REFUSALS: readonly string[] = ['Invalid email or password', 'Invalid credentials'];

/**
 * "Nothing at the address answered", kept as what failed so the screen writes it in the reader's
 * language (I-2143). In the iPhone and iPad app no address is shown (F-0237): a deployment is known
 * there by its name, and choosing another is the app's own screen, not this page's.
 */
export function deploymentUnreachableMessage(
  address = effectiveDeploymentAddress(),
): HeldSentenceType {
  if (isShellRuntime()) return sentenceToSay('auth.signInFailure.unreachableApp');
  return sentenceToSay('auth.signInFailure.unreachable', {
    address: deploymentAddressLabel(address),
  });
}

/**
 * T-1144: what the sign-in page's Details shows: the address nothing answered at. `null` in the iPhone
 * and iPad app, which names no address (F-0237).
 */
export function unreachableAddressDetail(address = effectiveDeploymentAddress()): string | null {
  return isShellRuntime() ? null : deploymentAddressLabel(address);
}

/**
 * The sentence to show when signing in fails.
 *
 * Order matters: unreachable first, because a deployment that never answered has no opinion about
 * the credentials and its silence must not be reported as one.
 */
export function signInFailureMessage(
  error: unknown,
  action: MessageKey = 'auth.actions.signIn',
): HeldSentenceType {
  // I-2151: a link this app did not start says so in the reader's language, not in the error's English.
  if (error instanceof DesktopForeignCodeError) {
    return sentenceToSay('auth.signInFailure.foreignDesktopCode');
  }
  if (isDeploymentUnreachableError(error)) {
    return [deploymentUnreachableMessage(), sentenceToSay('auth.signInFailure.neverChecked')];
  }
  const message = serverMessage(error);
  const status = (error as ITransportFailure | null)?.response?.status;
  // A refusal of the credentials is the catalogue's sentence, never the server's English one (I-2143):
  // by its code (I-2439), or by the sentence an older deployment sends without one.
  const code = (error as ITransportFailure | null)?.response?.data?.code;
  if (
    status === 401 &&
    (code === SIGNED_OUT_REFUSALS.INVALID_CREDENTIALS ||
      !message ||
      KNOWN_REFUSALS.includes(message.trim()))
  ) {
    return sentenceToSay('auth.signInFailure.noMatch');
  }
  if (message) return message;
  // Anything else is described, never repeated: a thrown error's own text is not written for the
  // person signing in (I-0398).
  return failureToSay(error, action);
}

/**
 * I-2150: what the waiting screen says when the saved session could not be checked, kept as what
 * failed so it is written in the reader's language: nothing at the address answered, or the
 * deployment answered something else and may still be starting.
 */
export function sessionVerificationFailure(error: unknown): HeldSentenceType {
  // I-2572 (MS-2): the desktop keychain did not answer. The sign-in is kept, and Retry asks again.
  if (error instanceof DesktopKeychainUnavailableError) {
    return sentenceToSay('auth.signInFailure.keychainUnavailable');
  }
  return isDeploymentUnreachableError(error)
    ? deploymentUnreachableMessage()
    : sentenceToSay('auth.signInFailure.couldNotVerify');
}
